import { useCallback, useMemo } from "react"

import { LabelColor } from "@/v2/graphql/admin-generated-rq"
import {
  useChangeSiteLabelColorMutation,
  useDeleteSiteLabelMutation,
  useOrgSiteLabelsBySlugQuery,
  useRenameSiteLabelMutation,
} from "@/v2/graphql/public-generated-rq"
import { useToast } from "@lumen-energy/design-system"

/**
 * Hooks to handle operations on Site Labels for a given Org.
 */
interface useOrgLabelsOpsProps {
  // function to refetch site labels for all sites in scope to make sure the label updates and
  // deletions are reflected for the `LabelGroup`'s site.
  refetchSites?: () => void
  // Whether or not we fetch the site labels for the org. This is useful for when we want to
  // a component may want to conditionally fetch the site labels for the org.
  enabled?: boolean
  orgSlug?: string
}

const useOrgLabelsOps = ({
  refetchSites,
  enabled = true,
  orgSlug,
}: useOrgLabelsOpsProps) => {
  const toast = useToast()

  const {
    data: orgSiteLabelsData,
    isLoading: orgSiteLabelsIsLoading,
    refetch: refetchOrgSiteLabels,
  } = useOrgSiteLabelsBySlugQuery(
    { orgSlug: orgSlug },
    {
      enabled: enabled && !!orgSlug,
      staleTime: 30 * 60_000,
    },
  )

  const orgLabels = useMemo(
    () => orgSiteLabelsData?.orgLabels || [],
    [orgSiteLabelsData?.orgLabels],
  )

  const renameSiteLabelMutation = useRenameSiteLabelMutation()
  const handleEditLabelName = useCallback(
    async (siteLabelPid: string, newLabelName: string) => {
      if (!orgSlug) {
        return
      }
      const promise = renameSiteLabelMutation.mutateAsync({
        input: { slug: orgSlug, siteLabelPid, newLabelName },
      })
      toast.promise(promise, {
        loading: "Renaming label...",
        success: `Label renamed to "${newLabelName}"`,
        error: (err) => err.toString(),
      })
      promise.then(() => {
        refetchOrgSiteLabels()
        refetchSites?.()
      })
    },
    [renameSiteLabelMutation, orgSlug, toast, refetchOrgSiteLabels, refetchSites],
  )

  const changeSiteLabelColorMutation = useChangeSiteLabelColorMutation()
  const handleEditLabelColor = useCallback(
    async (siteLabelPid: string, newLabelColor: LabelColor) => {
      if (!orgSlug) {
        return
      }
      const promise = changeSiteLabelColorMutation.mutateAsync({
        input: { slug: orgSlug, siteLabelPid, newLabelColor },
      })
      toast.promise(promise, {
        loading: "Changing label color...",
        success: `Label color changed`,
        error: "Error changing label color",
      })
      await promise
      await refetchOrgSiteLabels()
      refetchSites?.()
    },
    [changeSiteLabelColorMutation, refetchOrgSiteLabels, refetchSites, toast, orgSlug],
  )

  const deleteSiteLabelMutation = useDeleteSiteLabelMutation()
  const handleDeleteLabel = useCallback(
    async (siteLabelPid: string) => {
      if (!orgSlug) {
        return
      }
      const promise = deleteSiteLabelMutation.mutateAsync({
        input: { slug: orgSlug, siteLabelPid },
      })
      toast.promise(promise, {
        loading: "Deleting label...",
        success: `Label deleted`,
        error: "Error deleting label",
      })
      await promise
      await refetchOrgSiteLabels()
      refetchSites?.()
    },
    [deleteSiteLabelMutation, refetchOrgSiteLabels, refetchSites, toast, orgSlug],
  )

  return {
    handleDeleteLabel,
    handleEditLabelColor,
    handleEditLabelName,
    orgLabels,
    orgSiteLabelsIsLoading,
    refetchOrgSiteLabels,
  }
}

export default useOrgLabelsOps
